import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { groupByCategory } from "../lib/sections";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "./_shared";
import { Plus, Trash2, Package, FileText, RotateCcw, AlertTriangle, CheckCircle2, X, ChefHat, Eye } from "lucide-react";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/categories";
import FranchiseOrderForm from "../components/FranchiseOrderForm";
import OrderDetailsDialog from "../components/OrderDetailsDialog";
import AdminOrderEditor from "../components/AdminOrderEditor";
import useLiveSync from "../lib/useLiveSync";
import { formatDate } from "../lib/dates";

const COLUMNS = [
  { key: "submitted",          label: "Submitted",        next: "sent_to_production", nextLabel: "Send to Production" },
  { key: "sent_to_production", label: "Sent to Production", next: "pulled",           nextLabel: "Mark Pulled" },
  { key: "pulled",             label: "Pulled",           next: "ready_for_pickup",  nextLabel: "Mark Ready for Pickup" },
  { key: "ready_for_pickup",   label: "Ready for Pickup", next: "picked_up",         nextLabel: "Mark Picked Up" },
  { key: "picked_up",          label: "Picked Up",        next: null,                nextLabel: null },
];

const PKG_STATUS = {
  pending:   { label: "Pending",   dot: "bg-[hsl(var(--accent-taupe))]",  card: "border-[hsl(var(--accent-taupe))]/30",   icon: "text-[hsl(var(--accent-taupe))]" },
  approved:  { label: "Approved",  dot: "bg-[hsl(var(--primary))]",       card: "border-[hsl(var(--primary))]/30",        icon: "text-[hsl(var(--primary))]" },
  fulfilled: { label: "Fulfilled", dot: "bg-foreground",                  card: "border-foreground/20",                   icon: "text-foreground" },
  rejected:  { label: "Rejected",  dot: "bg-destructive",                 card: "border-destructive/30",                  icon: "text-destructive" },
};

export default function OrdersHub() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEmployee = user?.role === "employee";
  const [params, setParams] = useSearchParams();
  const defaultTab = isAdmin ? "incoming" : (isEmployee ? "mine" : "place");
  const tab = params.get("tab") || defaultTab;

  const setTab = (t) => setParams({ tab: t });

  const tabs = isAdmin
    ? [
        // Feb 2026: Packaging is placed alongside product orders in Place
        // Order — no separate Packaging tab on admin either. The endpoint
        // and PackagingPanel component still exist for legacy data access.
        // "Ready to Invoice" tab removed — all invoice actions live in /invoices.
        { k: "incoming", label: "Incoming Orders" },
        { k: "place", label: "Place Order" },
      ]
    : isEmployee
      ? [
          { k: "mine", label: "My Orders" },
        ]
      : [
          // Franchisee: Packaging tab removed — packaging is included in
          // the Place Order form's own Packaging Re-order block.
          { k: "place", label: "Place Order" },
          { k: "mine", label: "My Orders" },
          ...(user?.warehouse ? [{ k: "fulfill", label: "Fulfillment" }] : []),
        ];

  return (
    <Layout>
      <PageHeader
        eyebrow="Orders"
        title="Orders & Fulfillment"
        description="Everything that flows through the kitchen — in one place."
      />

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="orders-tabs">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`h-11 px-5 rounded-full text-sm font-medium ${
              tab === t.k ? "bg-foreground text-background" : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "incoming" && !isEmployee && <IncomingPanel canAdvance={isAdmin} />}
      {tab === "place" && !isEmployee && <PlaceOrderPanel />}
      {tab === "mine" && <MyOrdersPanel />}
      {tab === "fulfill" && user?.warehouse && <MyOrdersPanel fulfillment />}
      {tab === "packaging" && !isEmployee && isAdmin && <PackagingPanel canApprove={isAdmin} />}
    </Layout>
  );
}

// ---------------- Incoming (admin) ----------------
function IncomingPanel({ canAdvance }) {
  const [orders, setOrders] = useState([]);
  const [params, setParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useLiveSync(load);

  useEffect(() => {
    const oid = params.get("order"); if (oid) setDetail(oid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const closeDetail = () => {
    setDetail(null);
    if (params.get("order")) { params.delete("order"); setParams(params, { replace: true }); }
  };

  const advance = async (o, status) => {
    try {
      await api.patch(`/orders/${o.id}/status`, { status });
      toast.success(`${o.order_no} → ${COLUMNS.find(c => c.key === status)?.label}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2" data-testid="orders-kanban">
      {COLUMNS.map((col) => {
        const cards = orders.filter((o) => o.status === col.key);
        return (
          <div key={col.key} className="min-w-[280px] w-[280px] shrink-0" data-testid={`column-${col.key}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="label-eyebrow">{col.label}</div>
              <span className="text-xs text-muted-foreground">{cards.length}</span>
            </div>
            <div className="space-y-3 min-h-[200px]">
              {cards.map((o) => (
                <div key={o.id} onClick={() => setDetail(o.id)} className="luxury-card p-4 cursor-pointer hover:border-primary/30 transition-colors" data-testid={`order-card-${o.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium">{o.order_no}</div>
                    <div className="text-[11px] text-muted-foreground">{formatDate(o.pickup_date || o.created_at)}</div>
                  </div>
                  {o.customer && <div className="text-xs font-medium mb-1">{o.customer}</div>}
                  <CategorySummary items={o.items} />
                  {o.notes && <div className="text-[11px] italic text-muted-foreground border-t border-border pt-2 mb-2">&ldquo;{o.notes}&rdquo;</div>}
                  {canAdvance && col.next && (
                    <div className="mt-1 space-y-1.5">
                      <button onClick={(e) => { e.stopPropagation(); advance(o, col.next); }} data-testid={`advance-${o.id}`} className="h-9 w-full rounded-lg bg-foreground text-background text-xs font-medium hover:bg-foreground/90">{col.nextLabel}</button>
                      {(col.key === "submitted" || col.key === "sent_to_production") && (
                        <button onClick={(e) => { e.stopPropagation(); setEditing(o.id); }} data-testid={`edit-order-${o.id}`} className="h-9 w-full rounded-lg border border-border bg-card text-xs font-medium hover:bg-secondary">Edit Order</button>
                      )}
                    </div>
                  )}
                  {(!canAdvance || !col.next) && (
                    <button onClick={(e) => { e.stopPropagation(); setDetail(o.id); }} data-testid={`view-order-${o.id}`} className="mt-1 h-9 w-full rounded-lg border border-border bg-card text-xs font-medium hover:bg-secondary inline-flex items-center justify-center gap-1.5"><Eye className="h-3.5 w-3.5" /> See Order</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <OrderDetailsDialog orderId={detail} onClose={closeDetail} onSaved={load} />
      {editing && <AdminOrderEditor orderId={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
// ---------------- Place order ----------------
// Faithful digital twin of the printed Rice Village order form.
function PlaceOrderPanel() {
  return <FranchiseOrderForm />;
}

// ---------------- My orders (employee + franchisee) ----------------
function MyOrdersPanel({ fulfillment = false }) {
  const { user } = useAuth();
  const canComplete = user?.role === "employee" || fulfillment;
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = () => api.get(fulfillment ? "/orders?queue=fulfillment" : "/orders").then((r) => setOrders(r.data));
  useLiveSync(load);

  useEffect(() => {
    const oid = params.get("order"); if (oid) setDetail(oid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const closeDetail = () => { setDetail(null); if (params.get("order")) { params.delete("order"); setParams(params, { replace: true }); } };
  const takeOver = async (o) => {
    try {
      await api.post(`/orders/${o.id}/claim`);
      toast.success(`${o.order_no} is now yours to fulfill`, { description: "Kitchen has been told not to pull it." });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const grouped = useMemo(() => ({
    submitted:     orders.filter((o) => o.status === "submitted"),
    sentToProd:    orders.filter((o) => o.status === "sent_to_production"),
    pulled:        orders.filter((o) => o.status === "pulled"),
    readyForPickup:orders.filter((o) => o.status === "ready_for_pickup"),
    pickedUp:      orders.filter((o) => o.status === "picked_up"),
    // Feb 2026: retain full history on franchisee / kitchen "My Orders" so
    // completed orders don't vanish once they've been invoiced/paid.
    completed:     orders.filter((o) => ["ready_to_invoice", "invoiced", "paid"].includes(o.status)),
  }), [orders]);

  return (
    <div className="space-y-6" data-testid="my-orders">
      {grouped.submitted.length > 0 && !canComplete && (
        <section><div className="label-eyebrow mb-3">Submitted — awaiting kitchen</div>
          <div className="grid sm:grid-cols-2 gap-3">{grouped.submitted.map((o) => (<KitchenOrderCard key={o.id} order={o} onClick={() => setDetail(o.id)} />))}</div>
        </section>
      )}
      {grouped.submitted.length > 0 && canComplete && (
        <section data-testid="section-submitted"><div className="label-eyebrow mb-3">Submitted — awaiting admin approval <span className="text-[10px] opacity-60">· {grouped.submitted.length}</span></div>
          <div className="grid sm:grid-cols-2 gap-3">{grouped.submitted.map((o) => (<KitchenOrderCard key={o.id} order={o} onClick={() => setDetail(o.id)} />))}</div>
        </section>
      )}
      {grouped.sentToProd.length > 0 && (
        <section data-testid="section-sent-to-production"><div className="label-eyebrow mb-3">Sent to Production{canComplete ? " — pull next" : ""} <span className="text-[10px] opacity-60">· {grouped.sentToProd.length}</span></div>
          <div className="grid sm:grid-cols-2 gap-3">{grouped.sentToProd.map((o) => (
            <KitchenOrderCard key={o.id} order={o} onClick={() => setDetail(o.id)}
              onTakeOver={fulfillment && o.fulfillment_location_id !== user?.location_id ? () => takeOver(o) : null} />
          ))}</div>
        </section>
      )}
      {grouped.pulled.length > 0 && (
        <section data-testid="section-pulled"><div className="label-eyebrow mb-3">Pulled <span className="text-[10px] opacity-60">· {grouped.pulled.length}</span></div>
          <div className="grid sm:grid-cols-2 gap-3">{grouped.pulled.map((o) => (<KitchenOrderCard key={o.id} order={o} onClick={() => setDetail(o.id)} />))}</div>
        </section>
      )}
      {grouped.readyForPickup.length > 0 && (
        <section data-testid="section-ready-for-pickup"><div className="label-eyebrow mb-3">Ready for Pickup <span className="text-[10px] opacity-60">· {grouped.readyForPickup.length}</span></div>
          <div className="grid sm:grid-cols-2 gap-3">{grouped.readyForPickup.map((o) => (<KitchenOrderCard key={o.id} order={o} onClick={() => setDetail(o.id)} />))}</div>
        </section>
      )}
      {grouped.pickedUp.length > 0 && (
        <section data-testid="section-picked-up"><div className="label-eyebrow mb-3">Picked Up — today's record <span className="text-[10px] opacity-60">· {grouped.pickedUp.length}</span></div>
          <div className="grid sm:grid-cols-2 gap-3">{grouped.pickedUp.map((o) => (<KitchenOrderCard key={o.id} order={o} onClick={() => setDetail(o.id)} />))}</div>
        </section>
      )}
      {grouped.completed.length > 0 && !canComplete && (
        <section data-testid="my-orders-history">
          <div className="label-eyebrow mb-3">Order History — completed & invoiced</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {grouped.completed.slice(0, 50).map((o) => (<KitchenOrderCard key={o.id} order={o} onClick={() => setDetail(o.id)} />))}
          </div>
        </section>
      )}
      {orders.length === 0 && (
        <div className="luxury-card p-10 text-center text-sm text-muted-foreground">No orders yet.</div>
      )}
      <OrderDetailsDialog orderId={detail} onClose={closeDetail} allowEmployeeComplete={canComplete} onSaved={load} />
    </div>
  );
}

function KitchenOrderCard({ order, onClick, onTakeOver }) {
  if (onTakeOver) {
    return (
      <div className="luxury-card p-4 border-dashed" data-testid={`claimable-order-${order.id}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium">{order.order_no}</div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">At Kitchen</span>
        </div>
        <div className="text-xs text-muted-foreground mb-2">{order.customer || order.location_name}{order.pickup_date && ` · pickup ${formatDate(order.pickup_date)}`}</div>
        <CategorySummary items={order.items} />
        <button onClick={onTakeOver} data-testid={`take-over-${order.id}`}
          className="h-9 w-full rounded-lg bg-foreground text-background text-xs font-medium hover:bg-foreground/90 transition-colors">
          Take Over to Expedite
        </button>
      </div>
    );
  }
  return (
    <div onClick={onClick} className="luxury-card p-4 cursor-pointer hover:border-primary/30 transition-colors" data-testid={`my-order-card-${order.id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium">{order.order_no}</div>
        <StatusBadge status={order.status} />
      </div>
      <div className="text-xs text-muted-foreground mb-2">{order.customer || order.location_name}{order.pickup_date && ` · pickup ${formatDate(order.pickup_date)}`}</div>
      <CategorySummary items={order.items} />
      {order.notes && <div className="text-[11px] italic text-muted-foreground border-t border-border pt-2">&ldquo;{order.notes}&rdquo;</div>}
      <div className="mt-3 h-9 rounded-lg border border-border bg-card text-xs font-medium hover:bg-secondary inline-flex items-center justify-center gap-1.5 w-full"><Eye className="h-3.5 w-3.5" /> {["sent_to_production", "pulled", "ready_for_pickup"].includes(order.status) ? "Open & Update" : "See Order"}</div>
    </div>
  );
}
// ---------------- Packaging ----------------
function PackagingPanel({ canApprove }) {
  const [reqs, setReqs] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(() => ({ priority: "normal", notes: "", items: [{ _key: crypto.randomUUID(), name: "", quantity: 1 }] }));
  const [err, setErr] = useState("");
  const load = () => api.get("/packaging-requests").then((r) => setReqs(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const items = form.items.filter((i) => i.name.trim() && i.quantity > 0);
    if (!items.length) return setErr("Add at least one item.");
    try {
      const { data } = await api.post("/packaging-requests", { ...form, items });
      setOpenNew(false);
      toast.success(`Packaging request submitted`, { description: data.request_no });
      setForm({ priority: "normal", notes: "", items: [{ _key: crypto.randomUUID(), name: "", quantity: 1 }] });
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };
  const updateStatus = async (req, status) => {
    await api.patch(`/packaging-requests/${req.id}/status`, { status });
    const verb = { approved: "approved", rejected: "rejected", fulfilled: "fulfilled" }[status] || status;
    toast.success(`${req.request_no} ${verb}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setOpenNew(true)}
          data-testid="new-packaging-btn"
          className="h-11 px-5 rounded-xl bg-foreground text-background font-medium text-sm inline-flex items-center gap-2 hover:bg-foreground/90"
        >
          <Plus className="h-4 w-4" /> New Request
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="packaging-columns">
        {Object.keys(PKG_STATUS).map((s) => {
          const cards = reqs.filter((r) => r.status === s);
          const meta = PKG_STATUS[s];
          return (
            <div key={s}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                  <div className="label-eyebrow">{meta.label}</div>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">{cards.length}</span>
              </div>
              <div className="space-y-3 min-h-[120px]">
                {cards.map((r) => (
                  <div key={r.id} className={`luxury-card p-4 border-l-2 ${meta.card} fade-up`} data-testid={`packaging-card-${r.id}`}>
                    <div className="flex items-start justify-between mb-2.5">
                      <div className="text-sm font-medium inline-flex items-center gap-2">
                        <Package className={`h-3.5 w-3.5 ${meta.icon}`} /> {r.request_no}
                      </div>
                      {r.priority === "urgent" && (
                        <span className="text-[10px] uppercase tracking-wider text-destructive bg-destructive/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> Urgent
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">{r.location_name}</div>
                    <ul className="space-y-1 mb-2">
                      {r.items.slice(0, 3).map((it, idx) => (
                        <li key={`${r.id}-${it.name}-${idx}`} className="text-xs flex justify-between">
                          <span className="truncate pr-2">{it.name}</span>
                          <span className="text-muted-foreground tabular-nums">× {it.quantity}</span>
                        </li>
                      ))}
                      {r.items.length > 3 && <li className="text-[10px] text-muted-foreground">+ {r.items.length - 3} more</li>}
                    </ul>
                    {r.notes && <div className="text-[11px] italic text-muted-foreground border-t border-border pt-2 mb-2">&ldquo;{r.notes}&rdquo;</div>}
                    {r.approved_by_name && r.status !== "pending" && (
                      <div className="text-[10px] text-muted-foreground mb-2 inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> {r.status === "rejected" ? "Reviewed" : "Approved"} by {r.approved_by_name}
                      </div>
                    )}
                    {canApprove && r.status === "pending" && (
                      <div className="flex gap-2">
                        <button onClick={() => updateStatus(r, "approved")} data-testid={`approve-packaging-${r.id}`}
                          className="flex-1 h-9 rounded-lg bg-foreground text-background text-xs font-medium hover:bg-foreground/90 transition-all active:scale-[0.99]">
                          Approve
                        </button>
                        <button onClick={() => updateStatus(r, "rejected")} data-testid={`reject-packaging-${r.id}`}
                          className="h-9 px-3 rounded-lg border border-destructive/40 text-destructive text-xs font-medium hover:bg-destructive/5 transition-all">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    {canApprove && r.status === "approved" && (
                      <button onClick={() => updateStatus(r, "fulfilled")} data-testid={`advance-packaging-${r.id}`}
                        className="mt-1 h-9 w-full rounded-lg bg-foreground text-background text-xs font-medium hover:bg-foreground/90 transition-all active:scale-[0.99]">
                        Mark Fulfilled
                      </button>
                    )}
                  </div>
                ))}
                {cards.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-8 border border-dashed border-border rounded-xl">
                    —
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openNew && (
        <div className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setOpenNew(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-2xl border border-border w-full max-w-md p-8" data-testid="new-packaging-modal">
            <h2 className="font-display text-2xl font-light tracking-tight mb-1">Packaging Request</h2>
            <p className="text-sm text-muted-foreground mb-5">Request supplies from corporate.</p>
            <form onSubmit={submit} className="space-y-3">
              <div className="flex gap-2">
                {["normal", "urgent"].map((p) => (
                  <button type="button" key={p}
                    onClick={() => setForm({ ...form, priority: p })}
                    data-testid={`priority-${p}`}
                    className={`h-10 flex-1 rounded-full text-sm font-medium ${form.priority === p ? "bg-foreground text-background" : "bg-card border border-border"}`}>
                    {p}
                  </button>
                ))}
              </div>
              {form.items.map((it, idx) => (
                <div key={it._key} className="flex gap-2">
                  <input
                    value={it.name}
                    onChange={(e) => { const items = [...form.items]; items[idx] = { ...it, name: e.target.value }; setForm({ ...form, items }); }}
                    data-testid={`pkg-item-name-${idx}`}
                    placeholder="Item name"
                    className="h-11 flex-1 rounded-xl border border-input bg-card px-3 text-sm" />
                  <input type="number" min="1" value={it.quantity}
                    onChange={(e) => { const items = [...form.items]; items[idx] = { ...it, quantity: parseInt(e.target.value || "1", 10) }; setForm({ ...form, items }); }}
                    data-testid={`pkg-item-qty-${idx}`}
                    className="h-11 w-20 rounded-xl border border-input bg-card px-3 text-sm text-center" />
                </div>
              ))}
              <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { _key: crypto.randomUUID(), name: "", quantity: 1 }] })}
                data-testid="add-pkg-item-btn"
                className="text-sm text-muted-foreground hover:text-foreground">+ Add item</button>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2} placeholder="Notes (optional)" data-testid="pkg-notes"
                className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm" />
              {err && <div className="text-xs text-destructive">{err}</div>}
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setOpenNew(false)} className="h-11 flex-1 rounded-xl border border-border bg-card text-sm font-medium">Cancel</button>
                <button type="submit" data-testid="submit-packaging-btn" className="h-11 flex-1 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Ready to invoice ----------------
function ReadyToInvoicePanel() {
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const refresh = async () => {
    // Backend gate widened Feb 2026: invoices can be generated from `pulled`
    // onwards. Fetch each status in parallel and merge so the tab lists them all.
    const [pulled, ready, picked, i] = await Promise.all([
      api.get("/orders?status=pulled"),
      api.get("/orders?status=ready_for_pickup"),
      api.get("/orders?status=picked_up"),
      api.get("/invoices"),
    ]);
    setOrders([...(pulled.data || []), ...(ready.data || []), ...(picked.data || [])]);
    setInvoices(i.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const generate = async (o) => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/invoices", { order_id: o.id });
      toast.success("Invoice generated", { description: `${data.invoice_no} · $${data.total.toFixed(2)}` });
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const invoicedSet = new Set(invoices.map((i) => i.order_id));
  const pending = orders.filter((o) => !invoicedSet.has(o.id));

  return (
    <div>
      {err && <div className="text-sm text-destructive mb-3">{err}</div>}
      {pending.length === 0 ? (
        <div className="luxury-card p-12 text-center text-muted-foreground">
          <FileText className="h-6 w-6 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No fulfilled orders awaiting an invoice.</p>
          <Link to="/invoices" className="text-sm text-foreground mt-3 inline-block">View invoices →</Link>
        </div>
      ) : (
        <div className="luxury-card divide-y divide-border" data-testid="ready-to-invoice">
          {pending.map((o) => (
            <div key={o.id} className="p-5 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{o.order_no}{o.customer ? ` · ${o.customer}` : ""}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{o.items.length} items · fulfilled</div>
              </div>
              <button
                onClick={() => generate(o)}
                disabled={busy}
                data-testid={`generate-invoice-${o.id}`}
                className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-60"
              >
                Generate Invoice
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact per-category breakdown for order cards (matches inventory categories).
function CategorySummary({ items }) {
  return (
    <ul className="space-y-1 mb-2" data-testid="order-card-categories">
      {groupByCategory(items).map((g) => (
        <li key={g.cat} className="text-xs flex justify-between gap-2">
          <span className="truncate">{g.label} <span className="text-muted-foreground">· {g.items.length} flavor{g.items.length === 1 ? "" : "s"}</span></span>
          <span className="text-muted-foreground tabular-nums">× {g.items.reduce((t, it) => t + (it.quantity || 0), 0)}</span>
        </li>
      ))}
    </ul>
  );
}
